// Boost Blenz Admin — Messaging (SPEC §5.11): Releases & notices · What the app sends.
// Replaces the separate "Releases & notices" and "Push & Email" screens, keeping everything the
// old build did.
//
// ═══ TWO SEPARATE ACTIONS, STILL ═══════════════════════════════════════════════════
// 🔴 POSTING A NOTICE AND PUSHING IT ARE DIFFERENT WRITES. The design adds an "Also push it to
// phones now" toggle to the composer; it does NOT merge them. With the toggle on, Post opens the
// push confirmation — exact words, exact audience, cannot be unsent — and only then posts and
// pushes. A push can never happen as a side effect of one click. The tracker also refuses a
// second push of the same notice.
//
// ═══ WORD FOR WORD ════════════════════════════════════════════════════════════════
// The catalogue shows the text customers actually receive: the published App Copy line if there
// is one, else the app's built-in wording — served by /api/app/messaging, so it can't drift from
// App Copy. The WHEN / RULE lines were checked against the tracker repo on 2026-09-13; keep them
// in step with the code they name.
import React from "react";
import {
  useT, Icon, Card, Badge, FIGTREE, OSWALD, MONO, useNarrow, Body, SectionHead, Empty, Note, Pill, Tabs, Modal,
  Toggle, PlainInput, LabelledField, AppStat, fmtWhen, useToast,
} from "./ui";
import { useCachedJson } from "../../datacache";

type Level = "info" | "issue" | "critical";

/** Mirrors the app's NoticeSheet — accent, eyebrow and button, per level. */
const LEVEL: Record<Level, { label: string; accent: string; eyebrow: string; cta: string; hint: string; tone: string }> = {
  info: { label: "Heads up", accent: "#3c3291", eyebrow: "Heads up", cta: "Got it", hint: "News worth knowing. They can close it.", tone: "purple" },
  issue: { label: "Known issue", accent: "#f47f49", eyebrow: "Known issue", cta: "Got it", hint: "Something is broken and we know. Louder, still closable. Stays until you retire it.", tone: "warn" },
  critical: { label: "Recall / safety", accent: "#e0412f", eyebrow: "Important", cta: "I understand", hint: "No close button — they have to acknowledge it. A recall or a safety matter only.", tone: "bad" },
};
const levelOf = (v: unknown): Level => (v === "issue" || v === "critical" ? v : "info");

async function post(path: string, body: Record<string, unknown>) {
  const res = await fetch(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  const j = await res.json().catch(() => ({}));
  return res.ok && j?.ok ? { ok: true as const, body: j } : { ok: false as const, error: j?.error || `Refused (${res.status}).` };
}

/** What the customer sees. Wording exact, layout approximate — and labelled as such. */
function NoticePreview({ level, title, body, linkLabel, hasLink }: { level: Level; title: string; body: string; linkLabel: string; hasLink: boolean }) {
  const L = LEVEL[level];
  return (
    <div style={{ background: "#faf3e8", borderRadius: 18, padding: 18 }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 18, boxShadow: "0 10px 30px rgba(0,0,0,0.12)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: L.accent }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: L.accent }} />{L.eyebrow}
          </span>
          {level !== "critical" && <Icon name="x" size={15} color="#9b988f" />}
        </div>
        <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 21, letterSpacing: "0.02em", textTransform: "uppercase", lineHeight: 1.05, color: title ? "#1b1a19" : "#c9c4ba", marginTop: 12, overflowWrap: "anywhere" }}>{title || "Title"}</div>
        <div style={{ fontFamily: FIGTREE, fontSize: 13.5, lineHeight: 1.55, color: body ? "#3a3934" : "#b3ada2", marginTop: 7, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{body || "What you want them to know."}</div>
        {hasLink && <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: L.accent, marginTop: 10 }}>{linkLabel || "Read more"} →</div>}
        <div style={{ background: level === "critical" ? L.accent : "#000", color: "#fff", borderRadius: 999, textAlign: "center", fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, letterSpacing: "0.05em", textTransform: "uppercase", padding: "13px 0", marginTop: 16 }}>{L.cta}</div>
      </div>
      <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: "#6f6c65", textAlign: "center", marginTop: 12 }}>How it appears in the app · wording exact, layout approximate</div>
    </div>
  );
}

/** Push confirmation — reason, audience, the exact words. Used by the composer and each live row. */
function PushConfirm({ title, onCancel, onConfirm, busy, err, preface }: any) {
  const t = useT();
  const [audience, setAudience] = React.useState<"reminders" | "all">("reminders");
  const [reason, setReason] = React.useState("");
  return (
    <Modal title="Push this to phones?" onClose={onCancel} width={540}
      footer={<><Pill variant="ghost" onClick={onCancel}>Cancel</Pill>
        <Pill tone={audience === "all" ? "bad" : "accent"} icon="send" disabled={busy || reason.trim().length < 4} onClick={() => onConfirm({ audience, reason: reason.trim() })}>
          {busy ? "Sending…" : audience === "all" ? "Push to everyone" : "Push it"}
        </Pill></>}>
      {preface}
      <Body><strong style={{ color: t.fg }}>This can't be unsent, and it can only be sent once.</strong> Every phone below gets exactly this, as a notification: <em>“{title}”</em>.</Body>
      <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
        {([
          ["reminders", "People with reminders on", "Respects the switch they set. The right choice almost always."],
          ["all", "Everyone with the app", "Ignores their reminder setting. For a recall or a safety matter only — it's recorded that you chose it."],
        ] as const).map(([v, name, hint]) => (
          <label key={v} style={{ display: "flex", gap: 9, alignItems: "flex-start", cursor: "pointer" }}>
            <input type="radio" checked={audience === v} onChange={() => setAudience(v)} style={{ marginTop: 3 }} />
            <span>
              <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: v === "all" ? t.bad : t.fg }}>{name}</span>
              <span style={{ display: "block", fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>{hint}</span>
            </span>
          </label>
        ))}
      </div>
      <div style={{ marginTop: 14 }}>
        <LabelledField label="Why push it?" hint="required — logged against your name">
          <PlainInput value={reason} onChange={setReason} placeholder="e.g. Recall — customers need to stop today" />
        </LabelledField>
      </div>
      <Body size={12} color={t.muted} style={{ marginTop: 10 }}>Phones that never allowed notifications can't receive a push. They still see the notice when they open the app.</Body>
      {err && <div style={{ marginTop: 10, fontFamily: FIGTREE, fontSize: 12.5, color: t.bad }}>{err}</div>}
    </Modal>
  );
}

// ------------------------------------------------------------------ composer
function PostNotice({ onChanged, toast, narrow }: any) {
  const t = useT();
  const [level, setLevel] = React.useState<Level>("info");
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [linkUrl, setLinkUrl] = React.useState("");
  const [linkLabel, setLinkLabel] = React.useState("");
  const [startsAt, setStartsAt] = React.useState("");
  const [endsAt, setEndsAt] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [push, setPush] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const linkOk = !linkUrl || /^https:\/\/\S+$/.test(linkUrl.trim());
  const ready = title.trim().length >= 3 && body.trim().length >= 3 && reason.trim().length >= 4 && linkOk;

  const payload = () => ({
    level, title: title.trim(), body: body.trim(),
    linkUrl: linkUrl.trim() || null, linkLabel: linkLabel.trim() || null,
    // datetime-local has no zone; `new Date` reads it in the rep's own local time — the clock they typed against.
    startsAt: startsAt ? new Date(startsAt).toISOString() : null,
    endsAt: endsAt ? new Date(endsAt).toISOString() : null,
    reason: reason.trim(),
  });
  const reset = () => { setTitle(""); setBody(""); setLinkUrl(""); setLinkLabel(""); setStartsAt(""); setEndsAt(""); setReason(""); setPush(false); };

  async function postOnly() {
    setBusy(true); setErr(null);
    const res = await post("/api/app/notices", payload());
    setBusy(false);
    if (!res.ok) { setErr(res.error); return; }
    toast(`Posted as ${LEVEL[level].label} — in the app now, not pushed`);
    reset(); onChanged();
  }
  async function postAndPush({ audience, reason: pushReason }: any) {
    setBusy(true); setErr(null);
    const res = await post("/api/app/notices", payload());
    if (!res.ok) { setBusy(false); setErr(res.error); return; }
    const id = res.body?.id ?? res.body?.notice?.id;
    if (!id) { setBusy(false); setConfirming(false); toast("Posted — but push it from its row below; the new notice's id didn't come back", true); reset(); onChanged(); return; }
    const pr = await post(`/api/app/notices/${encodeURIComponent(id)}/push`, { audience, reason: pushReason });
    setBusy(false); setConfirming(false);
    if (pr.ok) toast(`Posted and pushed to ${pr.body.queued} phone${pr.body.queued === 1 ? "" : "s"}`);
    else toast(`Posted, but the push failed: ${pr.error} Try Push it now from its row.`, true);
    reset(); onChanged();
  }

  return (
    <Card>
      <SectionHead title="Post a notice" size={16} sub="Appears in the app for everyone who opens it — including people who never allowed notifications. Posting alone never pushes; that's the switch at the bottom." />
      <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "minmax(0,1.15fr) minmax(0,1fr)", gap: 22, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          <LabelledField label="How serious">
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {(Object.keys(LEVEL) as Level[]).map((k) => (
                <button key={k} onClick={() => setLevel(k)} aria-pressed={k === level}
                  style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, padding: "8px 15px", borderRadius: 999, cursor: "pointer", border: "1px solid " + (k === level ? "transparent" : t.border), background: k === level ? t.purple : "transparent", color: k === level ? "#1a1033" : t.muted }}>
                  {LEVEL[k].label}
                </button>
              ))}
            </div>
            <Body size={12.5} color={t.muted} style={{ marginTop: 4 }}>{LEVEL[level].hint}</Body>
          </LabelledField>
          <LabelledField label="Title" counter={[title.length, 120]}><PlainInput value={title} onChange={setTitle} maxLength={120} placeholder="e.g. We fixed the binder timer" /></LabelledField>
          <LabelledField label="Message" counter={[body.length, 600]}>
            <textarea value={body} maxLength={600} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="What happened, what they should do, and whether they need to do anything at all."
              style={{ width: "100%", background: t.appBg, border: "1px solid " + t.borderStrong, borderRadius: 10, padding: "10px 12px", fontFamily: FIGTREE, fontSize: 13.5, color: t.fg, outline: "none", boxSizing: "border-box", resize: "vertical" }} />
          </LabelledField>
          <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "1.4fr 1fr", gap: 14 }}>
            <LabelledField label="Link" hint="optional · https only"><PlainInput value={linkUrl} onChange={setLinkUrl} placeholder="https://boostblenz.com/…" style={linkOk ? {} : { borderColor: t.bad }} /></LabelledField>
            <LabelledField label="Link text"><PlainInput value={linkLabel} onChange={setLinkLabel} maxLength={60} placeholder="Read more" /></LabelledField>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "1fr 1fr", gap: 14 }}>
            <LabelledField label="Show from" hint="blank = now"><PlainInput type="datetime-local" value={startsAt} onChange={setStartsAt} /></LabelledField>
            <LabelledField label="Take down" hint="blank = until retired"><PlainInput type="datetime-local" value={endsAt} onChange={setEndsAt} /></LabelledField>
          </div>
          <LabelledField label="Why?" hint="required — logged against your name, never shown to customers"><PlainInput value={reason} onChange={setReason} placeholder="e.g. v1.0.1 fixes the overnight timer reset" /></LabelledField>
          <div style={{ background: t.appBg, border: "1px solid " + t.border, borderRadius: 12, padding: "13px 15px" }}>
            <Toggle on={push} onChange={setPush} label="Also push it to phones now" />
            <Body size={12} color={t.muted} style={{ marginTop: 7 }}>
              {push
                ? "You'll confirm the audience and the exact words before anything is sent. Anyone with notifications off still sees the notice when they open the app."
                : "Off: the notice shows in the app, but no phone buzzes. You can push it later from its row below."}
            </Body>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <Pill icon="spark" tone={level === "critical" ? "bad" : "accent"} disabled={!ready || busy} onClick={() => (push ? setConfirming(true) : postOnly())}>
              {busy ? "Posting…" : push ? "Post and push…" : "Post to the app"}
            </Pill>
            {!ready && <Body size={12} color={t.muted}>A title, a message and a reason.</Body>}
          </div>
          {err && !confirming && <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.bad }}>{err}</div>}
        </div>
        <LabelledField label="What they'll see">
          <NoticePreview level={level} title={title} body={body} linkLabel={linkLabel} hasLink={!!linkUrl.trim() && linkOk} />
        </LabelledField>
      </div>
      {confirming && (
        <PushConfirm title={title.trim()} busy={busy} err={err} onCancel={() => { setConfirming(false); setErr(null); }} onConfirm={postAndPush}
          preface={<Note icon="info" style={{ marginBottom: 14 }}>Posting first, then pushing. If the push fails, the notice stays posted and you can push it from its row.</Note>} />
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ notice rows
function statusOf(n: any): "live" | "scheduled" | "ended" | "retired" {
  const now = Date.now();
  if (n.retired_at) return "retired";
  if (n.ends_at && Date.parse(n.ends_at) < now) return "ended";
  if (n.starts_at && Date.parse(n.starts_at) > now) return "scheduled";
  return "live";
}

function LiveNotice({ n, i, reasons, canPost, onChanged, toast }: any) {
  const t = useT();
  const L = LEVEL[levelOf(n.level)];
  const st = statusOf(n);
  const [mode, setMode] = React.useState<null | "push" | "retire">(null);
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const why = (reasons || []).find((r: any) => r.action === "notice.create")?.reason;

  async function doPush({ audience, reason: r }: any) {
    setBusy(true); setErr(null);
    const res = await post(`/api/app/notices/${encodeURIComponent(n.id)}/push`, { audience, reason: r });
    setBusy(false);
    if (res.ok) { setMode(null); toast(`Pushed to ${res.body.queued} phone${res.body.queued === 1 ? "" : "s"}`); onChanged(); }
    else setErr(res.error);
  }
  async function doRetire() {
    setBusy(true); setErr(null);
    const res = await post(`/api/app/notices/${encodeURIComponent(n.id)}/retire`, { reason: reason.trim() });
    setBusy(false);
    if (res.ok) { setMode(null); setReason(""); toast("Retired — it's gone from the app"); onChanged(); }
    else setErr(res.error);
  }

  return (
    <div style={{ borderTop: i ? "1px solid " + t.border : "none", padding: "15px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <div style={{ minWidth: 240, flex: 1 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 7 }}>
            <Badge tone={L.tone}>{L.label}</Badge>
            {st === "scheduled" && <Badge tone="warn">Scheduled</Badge>}
            {n.push_sent_at ? <Badge tone="neutral">Pushed to {n.push_count} phone{n.push_count === 1 ? "" : "s"}</Badge> : <Badge tone="neutral">Not pushed</Badge>}
            <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>
              {n.starts_at ? fmtWhen(n.starts_at) : `Since ${fmtWhen(n.created_at)}`} · {n.ends_at ? `until ${fmtWhen(n.ends_at)}` : "until retired"}
            </span>
          </div>
          <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14.5, color: t.fg }}>{n.title}</div>
          <Body size={13} color={t.fg2} style={{ marginTop: 5, maxWidth: 640, whiteSpace: "pre-wrap" }}>{n.body}</Body>
          <Body size={11.5} color={t.muted} style={{ marginTop: 7 }}>
            {why ? <>Reason given: “{why}”</> : "No reason on record"}{n.actor ? ` · posted by ${String(n.actor).split("@")[0]}` : ""}
          </Body>
        </div>
        {canPost && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 150 }}>
            {!n.push_sent_at && <Pill size="sm" variant="soft" tone="purple" icon="send" onClick={() => { setMode("push"); setErr(null); }}>Push it now</Pill>}
            <Pill size="sm" variant="ghost" onClick={() => { setMode("retire"); setErr(null); }}>Retire</Pill>
          </div>
        )}
      </div>
      {mode === "push" && <PushConfirm title={n.title} busy={busy} err={err} onCancel={() => setMode(null)} onConfirm={doPush} />}
      {mode === "retire" && (
        <Modal title="Retire this notice?" onClose={() => setMode(null)}
          footer={<><Pill variant="ghost" onClick={() => setMode(null)}>Cancel</Pill><Pill disabled={busy || reason.trim().length < 4} onClick={doRetire}>{busy ? "Retiring…" : "Retire it"}</Pill></>}>
          <Body>Takes “{n.title}” down from every phone the next time the app checks. The record stays here.{n.push_sent_at ? " A notification already delivered stays on their lock screen — that can't be recalled." : ""}</Body>
          <div style={{ marginTop: 14 }}>
            <LabelledField label="Why take it down?" hint="required"><PlainInput value={reason} onChange={setReason} placeholder="e.g. Fixed in v1.0.2" /></LabelledField>
          </div>
          {err && <div style={{ marginTop: 10, fontFamily: FIGTREE, fontSize: 12.5, color: t.bad }}>{err}</div>}
        </Modal>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ catalogue
type Entry = {
  name: string;
  channel: "On the phone" | "From the server" | "Klaviyo" | "Email from the server";
  titleKey?: string; bodyKey?: string; msgTitle?: string; msgBody?: string;
  when: string; rule: string; warn?: boolean; tech: string; ownedBy: string;
};
const CATALOGUE: Entry[] = [
  { name: "Morning detox reminder", channel: "On the phone", titleKey: "notif.daily.title", bodyKey: "notif.daily.body",
    when: "Every morning at their own reminder time, on each day of a detox they've started. The phone schedules 14 days ahead.",
    rule: "Skipped if today's dose is already logged as taken. Nothing is scheduled while reminders are off, on a break, before a detox starts, or after it ends.",
    tech: "notif.daily.title / notif.daily.body · mobile/src/lib/notifications.ts · runSync", ownedBy: "App Copy" },
  { name: "Time to start", channel: "On the phone", titleKey: "notif.start.title", bodyKey: "notif.start.body",
    when: "Every morning at their reminder time for the next detox that hasn't started — from the date they picked or the day a break ends, or the day after the current detox finishes.",
    rule: "Stops the morning they log the first dose. Never for a detox that's parked or waiting on a decision. One a day.",
    tech: "notif.start.title / notif.start.body · mobile/src/lib/notifications.ts · scheduleStartNudges, startNudgeFrom", ownedBy: "App Copy" },
  { name: "Binder day · start your timer", channel: "On the phone", titleKey: "notif.binder.eat.title", bodyKey: "notif.binder.eat.body",
    when: "45 minutes after today's dose is logged, on a binder day — every other day from day 2 of Liver, Kidney and Parasite; never Heavy Metal.",
    rule: "Only while the binder timer hasn't started. Never for someone who fasts — their timer starts from the dose.",
    tech: "notif.binder.eat.title / notif.binder.eat.body · mobile/src/lib/notifications.ts · scheduleBinderDay", ownedBy: "App Copy" },
  { name: "Binder day · window open", channel: "On the phone", titleKey: "notif.binder.window.title", bodyKey: "notif.binder.window.body",
    when: "Two hours after the binder timer starts — when they said they'd eaten, or at dose time for someone who fasts.",
    rule: "Binder days only. Skipped once the binder is logged or the time has passed.",
    tech: "notif.binder.window.title / notif.binder.window.body · mobile/src/lib/notifications.ts · scheduleBinderDay", ownedBy: "App Copy" },
  { name: "Ready for the next round", channel: "On the phone", titleKey: "notif.redetox.title", bodyKey: "notif.redetox.body",
    when: "Once, on the date they pick on the Program complete screen — 1, 3 or 6 months out.",
    rule: "One-shot. Sent even when reminders are off — it only needs notification permission, because they asked for it.",
    tech: "notif.redetox.title / notif.redetox.body · mobile/src/lib/notifications.ts · scheduleRedetox", ownedBy: "App Copy" },
  { name: "Morning detox reminder · fallback", channel: "From the server", titleKey: "notif.daily.title", bodyKey: "notif.daily.body",
    when: "Every half hour the server checks for anyone whose reminder slot it is, in their own timezone.",
    rule: "ONLY when their account hasn't synced for 14 days — so the phone's own reminders have run out — and never when reminders are off or today's dose is taken. One a day. A safety net, not a second copy.",
    warn: true, tech: "the same notif.daily.* keys, resolved server-side · api/src/protocol.ts · dueDailyReminders → api/src/reminders.ts", ownedBy: "App Copy" },
  { name: "Notice push", channel: "From the server", msgTitle: "Whatever you typed", msgBody: "The title and message of the notice, word for word. Nothing is added or rewritten.",
    when: "Only when someone on the team pushes a notice from Releases & notices.",
    rule: "Once per notice, never twice. By default it goes to people with reminders on; “Everyone” ignores that switch, for a recall or a safety matter.",
    tech: "api/src/adminapi.ts · POST /admin/notices/:id/push → queueNoticePush", ownedBy: "This page" },
  { name: "Lifecycle milestones", channel: "Klaviyo", msgTitle: "Wording lives in Klaviyo", msgBody: "One event per milestone — program started, finished a detox, finished the program, start due, fell behind. Klaviyo owns the subject line and the design.",
    when: "Started (first dose) · finished a detox (last-day dose) · finished the plan · start due (the morning they picked) · fell behind (3+ days with no dose).",
    rule: "Recorded once per milestone, held 2 minutes so an undo can cancel it, then handed to Klaviyo. As of Sep 13, 2026 the Klaviyo Flows aren't built, so no email goes out yet.",
    warn: true, tech: "Klaviyo Flows — not App Copy · api/src/protocol.ts · recordMilestones / syncKlaviyoPending", ownedBy: "Klaviyo Flows" },
  { name: "Sign-in code", channel: "Email from the server", msgTitle: "Your Boost Blenz code: 123456", msgBody: "“Your login code” — and that it expires in 10 minutes.",
    when: "When they ask for a code on the sign-in screen.",
    rule: "At most 5 codes per email and 20 per network in 10 minutes. The wording is written in the app's server code, not App Copy.",
    tech: "api/src/email.ts · sendLoginCode ← POST /auth/request-code", ownedBy: "App code" },
];

function CatalogueRow({ m, i, copy, narrow }: { m: Entry; i: number; copy: Record<string, { text: string; published: boolean }>; narrow: boolean }) {
  const t = useT();
  const [open, setOpen] = React.useState(false);
  const [tech, setTech] = React.useState(false);
  const tone = m.channel === "Klaviyo" ? "accent" : m.channel === "On the phone" ? "purple" : "good";
  const title = m.titleKey ? copy[m.titleKey]?.text ?? m.titleKey : m.msgTitle;
  const body = m.bodyKey ? copy[m.bodyKey]?.text ?? m.bodyKey : m.msgBody;
  const edited = (m.titleKey && copy[m.titleKey]?.published) || (m.bodyKey && copy[m.bodyKey]?.published);
  const msg = (
    <span style={{ minWidth: 0, display: "block" }}>
      <span style={{ display: "block", fontFamily: FIGTREE, fontWeight: 600, fontSize: 13.5, color: t.fg, whiteSpace: "pre-wrap" }}>{title}</span>
      <span style={{ display: "block", fontFamily: FIGTREE, fontSize: 13, lineHeight: 1.5, color: t.fg2, marginTop: 3, whiteSpace: "pre-wrap" }}>{body}</span>
    </span>
  );
  return (
    <div style={{ borderTop: i ? "1px solid " + t.border : "none" }}>
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open}
        style={{ display: "grid", gridTemplateColumns: narrow ? "1fr auto" : "minmax(170px,1fr) minmax(0,1.6fr) auto", gap: 16, alignItems: "start", width: "100%", textAlign: "left", background: "none", border: "none", padding: "15px 0", cursor: "pointer" }}>
        <span style={{ display: "flex", flexDirection: "column", gap: 7, minWidth: 0 }}>
          <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14, color: t.fg }}>{m.name}</span>
          <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Badge tone={tone}>{m.channel}</Badge>
            {m.warn && <Badge tone={m.channel === "Klaviyo" ? "warn" : "bad"}>{m.channel === "Klaviyo" ? "Not sending yet" : "Safety net"}</Badge>}
          </span>
        </span>
        {!narrow && msg}
        <Icon name="chevron" size={15} color={t.muted} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 160ms", marginTop: 3 }} />
      </button>
      {narrow && <div style={{ paddingBottom: 14, marginTop: -6 }}>{msg}</div>}
      {open && (
        <div style={{ paddingBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 9 }}>
            <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, color: t.muted, flexShrink: 0, paddingTop: 1, width: 48 }}>WHEN</span>
            <Body size={13.5} style={{ color: t.fg }}>{m.when}</Body>
          </div>
          <div style={{ display: "flex", gap: 9 }}>
            <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, color: t.muted, flexShrink: 0, paddingTop: 1, width: 48 }}>RULE</span>
            <Body size={13.5} style={{ color: m.warn ? t.warn : t.fg2 }}>{m.rule}</Body>
          </div>
          <div style={{ display: "flex", gap: 9 }}>
            <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, color: t.muted, flexShrink: 0, paddingTop: 1, width: 48 }}>WORDS</span>
            <Body size={13.5}>Owned by {m.ownedBy}.{m.titleKey ? (edited ? " Showing your published App Copy." : " Showing the app's built-in wording — nothing published over it.") : ""}</Body>
          </div>
          <button onClick={() => setTech((x) => !x)} style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: t.purple }}>
            {tech ? "Hide technical detail" : "Show technical detail"}
          </button>
          {tech && <div style={{ fontFamily: MONO, fontSize: 11.5, color: t.muted, background: t.appBg, border: "1px solid " + t.border, borderRadius: 10, padding: "11px 13px", lineHeight: 1.6, wordBreak: "break-word" }}>{m.tech}</div>}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ page
export function Messaging({ tab, go }: { tab: string; go: (h: string) => void }) {
  const t = useT();
  const narrow = useNarrow();
  const [nonce, setNonce] = React.useState(0);
  const res = useCachedJson<any>(`/api/app/messaging${nonce ? `?v=${nonce}` : ""}`);
  const { toast, toastNode } = useToast();
  const d = res.data;
  const notices: any[] = d?.notices || [];
  const live = notices.filter((n) => ["live", "scheduled"].includes(statusOf(n)));
  const past = notices.filter((n) => !live.includes(n));
  const gate = d?.gate;
  const gateOn = !!(gate?.ios?.minimum || gate?.android?.minimum);
  const canPost = !!d?.canPost;
  const refresh = () => setNonce((v) => v + 1);
  const active = tab === "catalogue" ? "catalogue" : "notices";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <SectionHead title="Messaging" size={24}
        sub="Two things that belong together: notices you post into the app, and the catalogue of every message the app sends on its own."
        right={<Tabs value={active} onChange={(id: string) => go("#/app/messaging" + (id === "catalogue" ? "?tab=catalogue" : ""))} items={[{ id: "notices", label: "Releases & notices" }, { id: "catalogue", label: "What the app sends" }]} />} />
      {d && !canPost && (
        <Note icon="lock" tone="warn" label="Read-only for your role">
          You can read everything here, so you can tell a customer what to expect. Posting and pushing notices is for Ops and Owner.
        </Note>
      )}
      {res.error && !d && (
        <Card><Empty icon="alert" title="Couldn't load Messaging" action={<Pill icon="refresh" onClick={res.refresh}>Retry</Pill>}>The dashboard couldn't reach the app's release data.</Empty></Card>
      )}
      {d && d.ok === false && <Note icon="alert" tone="bad" label="The tracker didn't answer">{d.error} Notices below may be missing — try Refresh.</Note>}

      {active === "notices" ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "repeat(3,minmax(0,1fr))", gap: 14 }}>
            <AppStat label="Live notices" value={d ? live.length : "—"} icon="bell" accent={live.length ? t.warn : t.fg}
              sub={live.length ? "showing in the app now" : "customers see no notice at all"} meaning="A notice everyone sees when they open the app." />
            <AppStat label="Pushed · all time" value={d ? notices.filter((n) => n.push_sent_at).length : "—"} icon="send" accent={t.purple}
              sub="sent to phones" meaning="Notices that were also pushed, so phones buzzed." />
            <AppStat label="Update gate" value={gateOn ? "On" : "Off"} icon="lock" accent={gateOn ? t.bad : t.good}
              sub={gateOn ? `Blocked below iOS ${gate?.ios?.minimum ?? "—"} · Android ${gate?.android?.minimum ?? "—"}` : "nobody is blocked"}
              meaning="Set a minimum app version to force an update. On, anyone older is locked out until they update — for a broken build only. It needs an App Store and Play Store link first, so it isn't controlled from here yet." />
          </div>
          {canPost && <PostNotice onChanged={refresh} toast={toast} narrow={narrow} />}
          <Card>
            <SectionHead title="In the app now" size={16} sub={live.length ? "Live and scheduled notices, newest first. Retiring one removes it from the app." : undefined}
              right={<Pill variant="ghost" size="sm" icon="refresh" onClick={refresh}>Refresh</Pill>} />
            {res.loading && !d ? <Body color={t.muted}>Loading…</Body>
              : live.length ? live.map((n, i) => <LiveNotice key={n.id} n={n} i={i} reasons={d.reasons?.[n.id]} canPost={canPost} onChanged={refresh} toast={toast} />)
              : <Empty icon="check" title="Nothing showing">Customers see no notice at all right now.</Empty>}
          </Card>
          <Card>
            <SectionHead title="Retired notices" size={16} sub="Kept for the record — what was up, when, and why." />
            {past.length ? past.map((n, i) => {
              const rs = d.reasons?.[n.id] || [];
              const posted = rs.find((r: any) => r.action === "notice.create")?.reason;
              const retired = rs.find((r: any) => r.action === "notice.retire")?.reason;
              return (
                <div key={n.id} style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", borderTop: i ? "1px solid " + t.border : "none", padding: "13px 0", flexWrap: "wrap" }}>
                  <div style={{ minWidth: 220, flex: 1 }}>
                    <div style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 13.5, color: t.fg }}>{n.title}</div>
                    <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 4 }}>
                      {LEVEL[levelOf(n.level)].label} · {fmtWhen(n.starts_at || n.created_at)} → {fmtWhen(n.retired_at || n.ends_at)}
                      {posted ? ` · posted: “${posted}”` : ""}{retired ? ` · retired: “${retired}”` : ""}
                      {n.push_sent_at ? ` · pushed to ${n.push_count}` : ""}
                    </div>
                  </div>
                  <Badge tone="neutral">{statusOf(n) === "ended" ? "Ended" : "Retired"}</Badge>
                </div>
              );
            }) : <Body size={12.5} color={t.muted}>Nothing retired yet.</Body>}
          </Card>
        </>
      ) : (
        <Card>
          <SectionHead title="What the app sends" size={16}
            sub="The actual message each customer receives, word for word. Click a row for exactly when it fires and the rule that stops it firing twice." />
          {CATALOGUE.map((m, i) => <CatalogueRow key={m.name} m={m} i={i} copy={d?.copy || {}} narrow={narrow} />)}
          <Note icon="plug" label="Nothing here is editable on this screen" style={{ marginTop: 16 }}>
            Push wording comes from <button onClick={() => go("#/app/copy")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: t.purple, fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5 }}>App Copy</button>. Lifecycle email wording lives in Klaviyo Flows. Each row says which one owns its message, so nobody edits the wrong place.
          </Note>
        </Card>
      )}
      {toastNode}
    </div>
  );
}

